import RequirePermissions from '@/components/auth/requirePermissions';
import { AvlItem, useGetAvlItemsQuery } from '@/services/approvedVendorList';
import { useGetOrgEquipmentSettingsQuery, useSetOrgEquipmentSettingsMutation } from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Box, Button, CheckboxInput, Toggle } from '@palmetto/palmetto-components';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import {
  ApprovedVendorAllocation,
  ApprovedVendorSettingValue,
  OrganizationEquipmentSettingsV2,
  ProgramType,
  UserPermissions,
} from 'types';

import OrgHardwareEntrySelector from './OrgHardwareEntrySelector';
import { OrganizationSettingsCard } from './shared/OrganizationSettingsCard';

export interface OrganizationEquipmentSettingsProps {
  organization: OrganizationNode | undefined;
}

const allocationDisplayMap: Record<string, string> = {
  [ApprovedVendorAllocation.limited]: 'Limited',
  [ApprovedVendorAllocation.exception]: 'Exception',
  [ApprovedVendorAllocation.preApproval]: 'Pre-Approval',
  [ApprovedVendorAllocation.safeHarbor]: 'Safe Harbor',
};

const editableAllocations = [
  ApprovedVendorAllocation.limited,
  ApprovedVendorAllocation.exception,
  ApprovedVendorAllocation.preApproval,
  ApprovedVendorAllocation.safeHarbor,
];

const OrganizationEquipmentSettings = ({ organization }: OrganizationEquipmentSettingsProps) => {
  const {
    data: equipmentSettings,
    isLoading,
    error,
  } = useGetOrgEquipmentSettingsQuery({ alias: organization?.alias ?? '' }, { skip: !organization?.alias });

  const [setEquipmentSettings, { isLoading: isSaving }] = useSetOrgEquipmentSettingsMutation();

  const [inherit, setInherit] = useState(true);
  const [selectedAllocations, setSelectedAllocations] = useState<ApprovedVendorAllocation[]>([]);
  const [selectedEntryIds, setSelectedEntryIds] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const effectiveEntryIds = equipmentSettings?.value?.allowedEntryIds ?? [];

  // No equipmentType/orgAlias/accountId: matches V1's `includeException: true` full unfiltered catalog for this page.
  const { data: avlItems } = useGetAvlItemsQuery(
    { programType: ProgramType.solar },
    { skip: !isEditing && !effectiveEntryIds.length },
  );

  // OrgHardwareEntrySelector still expects the V1 `ApprovedVendorSettingValue` shape.
  const toSettingValue = (item: AvlItem): ApprovedVendorSettingValue => ({
    id: item.id,
    name: item.name,
    modelNumber: item.modelNumber,
    manufacturer: item.manufacturer,
    allocation: item.allocation,
    needsApproval: item.needsApproval,
    sku: item.sku,
    description: item.description,
    category: item.category,
    legacyIds: item.legacyIds,
  });

  const avlData = useMemo(() => {
    if (!avlItems) return undefined;
    const grouped: Record<string, ApprovedVendorSettingValue[]> = {};
    avlItems.data.forEach((item) => {
      (grouped[item.equipmentType] ??= []).push(toSettingValue(item));
    });
    return grouped;
  }, [avlItems]);

  useEffect(() => {
    if (equipmentSettings && !isEditing) {
      setInherit(equipmentSettings.inherit);
      setSelectedAllocations(equipmentSettings.value?.allowedAllocations ?? []);
      setSelectedEntryIds(equipmentSettings.value?.allowedEntryIds ?? []);
    }
  }, [equipmentSettings, isEditing]);

  const handleInheritChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInherit(event.target.checked);
    setSaveError(null);
  };

  const handleAllocationChange = (allocation: ApprovedVendorAllocation, checked: boolean) => {
    setSaveError(null);
    if (checked) {
      setSelectedAllocations((prev) => [...prev, allocation]);
    } else {
      setSelectedAllocations((prev) => prev.filter((a) => a !== allocation));
    }
  };

  const handleSave = async () => {
    if (!organization?.alias) return;
    setSaveError(null);

    try {
      const coveredAllocations = new Set<string>([...selectedAllocations, ApprovedVendorAllocation.full]);
      const filteredEntryIds = selectedEntryIds.filter((id) => {
        const allEntries = Object.values(avlData ?? {}).flat();
        const entry = allEntries.find((e) => e.id === id || e.legacyIds?.includes(id));
        return !entry || !coveredAllocations.has(entry.allocation);
      });

      const body: { value: OrganizationEquipmentSettingsV2; inherit: boolean } = {
        value: {
          allowedAllocations: selectedAllocations,
          allowedEntryIds: filteredEntryIds,
        },
        inherit,
      };
      await setEquipmentSettings({ alias: organization.alias, equipmentSettings: body }).unwrap();
      setIsEditing(false);
    } catch (err: any) {
      setSaveError(err?.data?.message ?? 'Failed to save settings');
    }
  };

  const handleCancel = () => {
    setInherit(equipmentSettings?.inherit ?? true);
    setSelectedAllocations(equipmentSettings?.value?.allowedAllocations ?? []);
    setSelectedEntryIds(equipmentSettings?.value?.allowedEntryIds ?? []);
    setIsEditing(false);
    setSaveError(null);
  };

  const effectiveAllocations = equipmentSettings?.value?.allowedAllocations ?? [];

  return (
    <RequirePermissions permissions={[UserPermissions.canImportAvlData]}>
      <OrganizationSettingsCard
        cardTitle="Equipment Allocation Settings"
        organization={organization}
        isLoading={isLoading}
        error={error}
        isInherited={!error && !isEditing && equipmentSettings?.inherit}
        onClick={() => setIsEditing(true)}
        renderEditButton={(button) => (
          <RequirePermissions permissions={[UserPermissions.canImportAvlData]}>
            {isEditing ? null : button}
          </RequirePermissions>
        )}
      >
        <Box padding="0 lg lg lg" gap="md">
          <Box color="body-secondary" fontSize="xs">
            Full allocation equipment is always shown. Select additional allocation statuses this organization's
            users can access.
          </Box>

          {isEditing ? (
            <Box gap="md">
              <Toggle
                id="inherit-equipment-settings"
                label="Inherit from parent organization"
                isChecked={inherit}
                onChange={handleInheritChange}
              />

              {inherit ? (
                <Box color="body-secondary" fontSize="xs" padding="sm 0 0 0">
                  Settings will be inherited from the parent organization.
                </Box>
              ) : (
                <Box gap="md">
                  <Box gap="sm">
                    <Box fontSize="sm" fontWeight="medium">
                      Allowed Allocations
                    </Box>
                    <Box color="body-secondary" fontSize="xs">
                      Full allocation equipment is always shown. Select additional allocation statuses this
                      organization's users can access.
                    </Box>
                    {editableAllocations.map((allocation) => (
                      <CheckboxInput
                        key={allocation}
                        id={`allocation-${allocation}`}
                        label={allocationDisplayMap[allocation]}
                        isChecked={selectedAllocations.includes(allocation)}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleAllocationChange(allocation, event.target.checked)
                        }
                      />
                    ))}
                  </Box>

                  <Box borderWidth="xs 0 0 0" borderColor="separator" padding="md 0 0 0">
                    <OrgHardwareEntrySelector
                      avlEntries={avlData ?? {}}
                      selectedEntryIds={selectedEntryIds}
                      onSelectionChange={setSelectedEntryIds}
                      allowedAllocations={selectedAllocations}
                    />
                  </Box>
                </Box>
              )}

              {saveError && (
                <Box color="danger" fontSize="sm">
                  {saveError}
                </Box>
              )}

              <Box direction="row" gap="sm" padding="sm 0 0 0">
                <Button variant="primary" size="sm" onClick={handleSave} isLoading={isSaving}>
                  Save
                </Button>
                <Button variant="secondary" tone="neutral" size="sm" onClick={handleCancel} isDisabled={isSaving}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box gap="md">
              <Box gap="xs">
                <Box fontSize="sm" fontWeight="medium">
                  Allowed Allocations
                </Box>
                {effectiveAllocations.length > 0 ? (
                  <Box direction="row" gap="sm" wrap>
                    {effectiveAllocations.map((allocation) => (
                      <Box key={allocation} padding="xs sm" background="primary-50" radius="sm" fontSize="sm">
                        {allocationDisplayMap[allocation] ?? allocation}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box color="body-secondary" fontSize="xs">
                    No additional allocation statuses enabled. Only full allocation equipment is shown.
                  </Box>
                )}
              </Box>

              {effectiveEntryIds.length > 0 && avlData && (
                <Box borderWidth="xs 0 0 0" borderColor="separator" padding="md 0 0 0">
                  <OrgHardwareEntrySelector
                    avlEntries={avlData}
                    selectedEntryIds={effectiveEntryIds}
                    onSelectionChange={() => {}}
                    allowedAllocations={effectiveAllocations}
                    disabled
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </OrganizationSettingsCard>
    </RequirePermissions>
  );
};

export default OrganizationEquipmentSettings;
